import React, { useState, useEffect, useContext } from "react";
import {
  VYLABS_BASE_URL,
  GET_CUSTOM_PORTALS,
  CREATE_CUSTOM_PORTALS,
  UPDATE_CUSTOM_PORTALS,
  DELETE__CUSTOM_PORTALS,
} from "../../helper/url_helper";
import { deleteRequest, get, post, put } from "../../helper/Api_helper";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import AnimationPlayer from "../../Common/Animation-Player/AnimationPlayer";
import NoDataFoundAnimation from "../../Assets/Json/nodata-found-robot-animation.json";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations";
import Button from "../../Common/Button/Button";
import ContentDialog from "../../Common/Content-Dialog/ContentDialog";
import TextInput from "../../Common/TextInput/TextInput";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextArea from "../../Common/TextArea/TextArea";
import Table from "../../Common/Table/Table";
import deleteIcon from "../../Assets/Images/cf-delete-icon-dark.svg";
import deleteLightIcon from "../../Assets/Images/cf-delete-icon-light.svg";
import addIcon from "../../Assets/Images/create-icon.png";
import { DynamicContext } from "../../Context/DynamicContext";
import ToggleSwitcher from "../../Common/Toggle-Switcher/ToggleSwitcher";
import { toCapitalize } from "../../Common/Common-Function/CommonFunctions";
import editIcon from "../../Assets/Images/cf-edit-icon-dark.svg";
import editLightIcon from "../../Assets/Images/cf-edit-icon-light.svg";
import SelectInput from "../../Common/Select-Input/SelectInput";

const PORTAL_OPTIONS = [
  { title: "Dice", value: "dice" },
  { title: "Foundit", value: "foundit" },
  { title: "MonsterUS", value: "monsterus" },
  { title: "IRMT", value: "irmt" },
];

function PortalIntegrations() {
  const { isDarkMode } = useContext(DynamicContext);
  const [isLoading, setIsLoading] = useState(true);
  const [portals, setPortals] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [portalToDelete, setPortalToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPortal, setEditingPortal] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const columns = [
    {
      name: "Portal Name",
      selector: (row) => toCapitalize(row.portal),
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Alias",
      selector: (row) => toCapitalize(row.alias),
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Status",
      selector: (row) => (row.enabled ? "Enabled" : "Disabled"),
      sortable: true,
      minWidth: "120px",
      cell: (row) => (
        <span
          style={{
            color: row.enabled ? "#4caf50" : "#f44336",
            fontWeight: "500",
          }}
        >
          {row.enabled ? "Enabled" : "Disabled"}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "80px",
          }}
        >
          <img
            src={isDarkMode ? editIcon : editLightIcon}
            alt="Edit"
            style={{
              cursor: "pointer",
              opacity: 0.7,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 1)}
            onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
            onClick={() => handleEditClick(row)}
          />
          <img
            src={isDarkMode ? deleteIcon : deleteLightIcon}
            alt="Delete"
            style={{
              cursor: "pointer",
              opacity: 0.7,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 1)}
            onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
            onClick={() => handleDeleteClick(row)}
          />
        </div>
      ),
      width: "100px",
      center: "true",
      ignoreRowClick: true,
    },
  ];

  const formik = useFormik({
    initialValues: {
      portalName: "",
      aliasName: "",
      portalBodyContent: "",
      enabled: true,
    },
    validationSchema: Yup.object({
      portalName: Yup.string()
        .required("Portal name is required")
        .test("is-valid-portal", "Invalid portal name", (value) => {
          return PORTAL_OPTIONS.some((option) => option.value === value);
        }),
      aliasName: Yup.string().required("Alias name is required"),
      portalBodyContent: Yup.string()
        .required("Portal body content is required")
        .test("is-valid-json", "Invalid JSON format", (value) => {
          try {
            JSON.parse(value);
            return true;
          } catch {
            return false;
          }
        }),
      enabled: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      if (editingPortal) {
        await updateCustomPortal(
          editingPortal.id,
          values.portalName,
          values.aliasName,
          values.portalBodyContent,
          values.enabled,
        );
      } else {
        await createCustomPortal(
          values.portalName,
          values.aliasName,
          values.portalBodyContent,
          values.enabled,
        );
      }
    },
  });

  const fetchCustomPortals = async () => {
    try {
      setIsLoading(true);
      const response = await get(VYLABS_BASE_URL, GET_CUSTOM_PORTALS);
      setPortals(response || []);
    } catch (err) {
      console.error("PublicCareerPortal GET_TENANT_DETAILS Error:", err);
      Toast("error", "Failed to fetch portal details");
      setPortals([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomPortals();
  }, []);

  const createCustomPortal = async (
    portalName,
    aliasName,
    portalBodyContent,
    enabled,
  ) => {
    try {
      setIsSubmitting(true);
      const parsedBodyContent = JSON.parse(portalBodyContent);
      const bodyContent = {
        portal: portalName,
        alias: aliasName,
        ...parsedBodyContent,
        enabled: enabled,
      };

      await post(VYLABS_BASE_URL, CREATE_CUSTOM_PORTALS, bodyContent);

      Toast("success", `Portal "${portalName}" created successfully`);
      setIsDialogOpen(false);
      formik.resetForm();
      fetchCustomPortals();
    } catch (error) {
      console.error("createCustomPortal Error:", error);
      Toast("error", error?.message || "Failed to create portal");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (portal) => {
    setEditingPortal(portal);
    formik.setValues({
      portalName: portal.portal || "",
      aliasName: portal.alias || "",
      portalBodyContent: JSON.stringify(portal, null, 2),
      enabled: portal.enabled !== undefined ? portal.enabled : true,
    });
    setIsEditDialogOpen(true);
  };

  const updateCustomPortal = async (
    portalId,
    portalName,
    aliasName,
    portalBodyContent,
    enabled,
  ) => {
    try {
      setIsUpdating(true);
      const parsedBodyContent = JSON.parse(portalBodyContent);
      const {
        portal,
        alias,
        enabled: _,
        ...cleanBodyContent
      } = parsedBodyContent;

      const bodyContent = {
        id: portalId,
        portal: portalName,
        alias: aliasName,
        ...cleanBodyContent,
        enabled: enabled,
      };

      await put(VYLABS_BASE_URL, UPDATE_CUSTOM_PORTALS, bodyContent);

      Toast("success", `Portal "${portalName}" updated successfully`);
      setIsEditDialogOpen(false);
      setEditingPortal(null);
      formik.resetForm();
      fetchCustomPortals();
    } catch (error) {
      console.error("updateCustomPortal Error:", error);
      Toast("error", error?.message || "Failed to update portal");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (portal) => {
    setPortalToDelete(portal);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!portalToDelete) return;

    try {
      setIsDeleting(true);
      await deleteRequest(VYLABS_BASE_URL, DELETE__CUSTOM_PORTALS, {
        id: portalToDelete.id,
      });

      Toast(
        "success",
        `Portal "${portalToDelete.portal}" deleted successfully`,
      );
      setIsDeleteDialogOpen(false);
      setPortalToDelete(null);
      fetchCustomPortals();
    } catch (error) {
      console.error("deleteCustomPortal Error:", error);
      Toast("error", error?.message || "Failed to delete portal");
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setPortalToDelete(null);
  };

  const handleAddPortalClick = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setIsEditDialogOpen(false);
    setEditingPortal(null);
    formik.resetForm();
    formik.setErrors({});
  };

  const renderContent = () => {
    if (portals && portals.length > 0) {
      return (
        <div style={{ padding: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div className="settings-title-container">
              <h2>Custom Portals</h2>
              <p>Create your custom portals</p>
            </div>
            <Button
              label={"Add Custom Portal"}
              event={handleAddPortalClick}
              iconSrc={addIcon}
              iconWidth={"13px"}
            />
          </div>

          <Table
            data={portals}
            columns={columns}
            pagination={true}
            paginationPerPage={10}
            paginationRowsPerPageOptions={[5, 10, 15, 20]}
            highlightOnHover={true}
            fixedHeader={true}
            fixedHeaderScrollHeight="500px"
            progressPending={isLoading}
            dataNotFoundMessage="No custom portals found"
          />
        </div>
      );
    }
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 20px",
          textAlign: "center",
        }}
      >
        <h3 style={{ marginTop: "20px", marginBottom: "10px" }}>
          No Custom Portals Found
        </h3>
        <AnimationPlayer
          animationSrc={NoDataFoundAnimation}
          playerHeight="200px"
          playerWidth="350px"
        />
        <p
          style={{
            color: "#666",
            marginBottom: "20px",
            maxWidth: "400px",
          }}
        >
          You haven't created any custom portals yet. Click the button below to
          get started.
        </p>
        <Button label={"Add Custom Portal"} event={handleAddPortalClick} />
      </div>
    );
  };

  return (
    <div>
      {isLoading ? <VybogTALLoaderAnimation /> : renderContent()}
      <ContentDialog
        isOpen={isDialogOpen || isEditDialogOpen}
        title={editingPortal ? "Edit Custom Portal" : "Add Custom Portal"}
        onClose={handleDialogClose}
        onCloseIconClose={handleDialogClose}
        event={formik.handleSubmit}
        submitButtonLabel={
          isSubmitting || isUpdating
            ? editingPortal
              ? "Updating..."
              : "Creating..."
            : editingPortal
              ? "Update Portal"
              : "Create Portal"
        }
        cancelButtonLabel="Cancel"
        isSubmitDisabled={!formik.isValid || isSubmitting || isUpdating}
        contentWidth="400px"
        overflowY={true}
      >
        <div
          className="portal-popup-container"
          style={{ margin: "10px", padding: "20px 0" }}
        >
          <div style={{ marginBottom: "20px" }}>
            <SelectInput
              options={PORTAL_OPTIONS}
              label="Portal Name"
              name="portalName"
              value={formik.values.portalName}
              onChange={(e) =>
                formik.setFieldValue("portalName", e.target.value)
              }
              onBlur={formik.handleBlur}
              defaultOption="Select Portal Name"
              error={formik?.touched?.portalName && formik?.errors?.portalName}
              errorMessage={formik?.errors?.portalName}
              required={true}
              selectStyle={{
                fontFamily: "Satoshi",
                fontSize: "15px",
                fontWeight: 500,
                color: "var(--header__color)",
                borderRadius: "12px",
                ...(editingPortal && {
                  pointerEvents: "none",
                  opacity: 0.7,
                  cursor: "not-allowed",
                }),
              }}
            />
          </div>
          <div style={{ marginBottom: "20px" }}>
            <TextInput
              label="Alias Name"
              name="aliasName"
              value={formik.values.aliasName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter Alias Name (e.g., Main Dice Account)"
              error={formik?.touched?.aliasName && formik?.errors?.aliasName}
              errorMessage={formik?.errors?.aliasName}
              required={true}
            />
          </div>
          {!editingPortal && (
            <div>
              <TextArea
                label={"Portal JSON Content"}
                name="portalBodyContent"
                labelStyle={{ color: "var(--text_color)" }}
                placeholder="Enter the Portal JSON Content "
                value={formik.values.portalBodyContent}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{
                  color: "var(--header__color)",
                  border: "1px solid var(--border__color)",
                  resize: "none",
                }}
                rows={4}
                error={
                  formik?.touched?.portalBodyContent &&
                  formik?.errors?.portalBodyContent
                }
                errorMessage={formik?.errors?.portalBodyContent}
                required={true}
              />
            </div>
          )}
          <div style={{ marginTop: "10px" }}>
            <ToggleSwitcher
              label="Enable This Portal"
              name="enabled"
              value={formik.values.enabled}
              onChange={(name, checked) => formik.setFieldValue(name, checked)}
              labelStyle={{ color: "var(--text_color)" }}
              required={false}
            />
          </div>
        </div>
      </ContentDialog>
      <ContentDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Portal"
        onClose={cancelDelete}
        onCloseIconClose={cancelDelete}
        event={confirmDelete}
        submitButtonLabel={isDeleting ? "Deleting..." : "Delete Portal"}
        cancelButtonLabel="Cancel"
        isSubmitDisabled={isDeleting}
        contentWidth="450px"
        submitButtonStyle={{
          backgroundColor: "#f44336",
          color: "white",
        }}
      >
        <div style={{ padding: "20px 10px", textAlign: "center" }}>
          <p style={{ marginBottom: "10px", fontSize: "16px" }}>
            Are you sure you want to delete the portal?
          </p>
          <p
            style={{
              fontWeight: "bold",
              fontSize: "18px",
              color: "#f44336",
              marginBottom: "10px",
            }}
          >
            "{portalToDelete?.portal}"
          </p>
          <p style={{ color: "#666", fontSize: "14px" }}>
            This action cannot be undone.
          </p>
        </div>
      </ContentDialog>
    </div>
  );
}

export default PortalIntegrations;
